import React, { useState } from 'react';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { ApiService } from '../../services/ApiService.ts';
import { useTranslation } from '../../i18n.ts';
import { useToast } from './Toast.tsx';
import { useSessionStore } from '../../stores/useSessionStore.ts';

interface InvitationNotification {
  id: string;
  data?: { token?: string; projectId?: string; [key: string]: unknown } | null;
  project_id?: string | null;
}

interface Props {
  notification: InvitationNotification;
  /** Wordt aangeroepen na succesvolle accept of decline (UI kan refetchen). */
  onHandled?: () => void;
  /** 'sm' = compact (notificatie-dropdown), 'md' = full-page list. */
  size?: 'sm' | 'md';
}

/**
 * Inline Accept/Decline knoppen voor type='invitation_received' notificaties.
 * Refs Nextcloud #499 — laat tester direct vanuit de notificatie reageren
 * zonder eerst de ProjectSwitcher te hoeven openen.
 */
const InvitationActions: React.FC<Props> = ({ notification, onHandled, size = 'sm' }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [busy, setBusy] = useState<'accept' | 'decline' | null>(null);

  /** Token uit notification.data; fallback voor oude notificaties zonder token. */
  const resolveToken = async (): Promise<string | null> => {
    const direct = notification.data?.token;
    if (direct) return direct;
    try {
      const invs = await ApiService.getPendingInvitations();
      const projectId = notification.data?.projectId || notification.project_id;
      const match = invs.find((i: { project_id: string }) => i.project_id === projectId);
      return (match as { token?: string })?.token || null;
    } catch {
      return null;
    }
  };

  const refreshInvitationsInStore = async () => {
    try {
      const fresh = await ApiService.getPendingInvitations();
      useSessionStore.getState().setPendingInvitations(fresh);
    } catch {
      /* best effort — store blijft op laatste bekende waarde */
    }
  };

  const run = async (kind: 'accept' | 'decline') => {
    setBusy(kind);
    const token = await resolveToken();
    if (!token) {
      toast(t('invitationFailed') || 'Could not process invitation', 'error');
      setBusy(null);
      return;
    }
    try {
      if (kind === 'accept') await ApiService.acceptInvitation(token);
      else await ApiService.declineInvitation(token);
      await refreshInvitationsInStore();
      toast(
        kind === 'accept'
          ? t('invitationAccepted') || 'Invitation accepted'
          : t('invitationDeclined') || 'Invitation declined',
        'success'
      );
      onHandled?.();
    } catch {
      toast(t('invitationFailed') || 'Could not process invitation', 'error');
    } finally {
      setBusy(null);
    }
  };

  const isMd = size === 'md';
  const iconSize = isMd ? 14 : 11;
  const btnPad = isMd ? 'px-3 py-1.5 text-xs' : 'px-2 py-1 text-[10px]';

  return (
    <div className="flex gap-1.5 mt-2" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          run('accept');
        }}
        disabled={!!busy}
        className={`${btnPad} flex items-center gap-1 bg-pt-forest text-pt-gold rounded-lg font-bold uppercase tracking-wider hover:bg-pt-forest/90 transition-colors disabled:opacity-50`}
      >
        {busy === 'accept' ? (
          <Loader2 size={iconSize} className="animate-spin" />
        ) : (
          <CheckCircle size={iconSize} />
        )}
        {t('accept') || 'Accepteren'}
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          run('decline');
        }}
        disabled={!!busy}
        className={`${btnPad} flex items-center gap-1 bg-white dark:bg-white/5 text-pt-sage-text dark:text-[var(--text-muted)] border border-pt-forest/10 dark:border-white/10 rounded-lg font-bold uppercase tracking-wider hover:bg-pt-forest/5 dark:hover:bg-white/10 transition-colors disabled:opacity-50`}
      >
        {busy === 'decline' ? (
          <Loader2 size={iconSize} className="animate-spin" />
        ) : (
          <XCircle size={iconSize} />
        )}
        {t('decline') || 'Afwijzen'}
      </button>
    </div>
  );
};

export default InvitationActions;
